import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { 
  User, 
  Globe, 
  History, 
  Plus, 
  Play, 
  Edit, 
  Trash2, 
  Clock, 
  Sparkles,
  BookOpen,
  Loader2,
  Star,
  Search,
  Filter,
  MessageCircle,
  Archive,
  FileText,
  X,
  RotateCcw,
  Heart,
  Users,
  UserPlus,
  Camera,
  Save
} from 'lucide-react'
import { useWorldSimulatorStore } from '../stores/worldSimulatorStore'
import { useStoryStore } from '../stores/storyStore'
import { usePlayCreateStore } from '../stores/playCreateStore'
import { useAuth } from '../stores/authStore'
import worldSimulatorStorageService from '../services/worldSimulatorStorageService'
import playCreateStorageService from '../services/playCreateStorageService'
import { UnifiedCard } from '../components/UnifiedCard'
import { CreationEntry } from '../components/CreationEntry'
import localCloudService from '../services/localCloudService'
import toast from 'react-hot-toast'

export const MyWorld = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { user, isAuthenticated, updateProfile, refreshUser } = useAuth()
  const { loadWorlds } = useWorldSimulatorStore()
  const { stories, deleteStory, initializeStore, updateStory } = useStoryStore()
  const { characters, loadCharacters } = usePlayCreateStore()
  
  // 用户统计数据
  const [userStats, setUserStats] = useState({
    likes: 0,
    followers: 0,
    following: 0,
    works: 0
  })
  
  // 分类标签 - 从URL参数读取，如果没有则默认为'stories'
  const [activeCategory, setActiveCategory] = useState(() => {
    const category = searchParams.get('tab') || searchParams.get('category')
    return ['stories', 'characters', 'worlds', 'history'].includes(category) ? category : 'stories'
  })
  
  // 监听URL参数变化，当用户通过浏览器返回按钮返回时恢复标签页状态
  useEffect(() => {
    const category = searchParams.get('tab') || searchParams.get('category')
    if (category && ['stories', 'characters', 'worlds', 'history'].includes(category) && category !== activeCategory) {
      setActiveCategory(category)
    }
  }, [searchParams, activeCategory])
  
  // 状态筛选
  const [statusFilter, setStatusFilter] = useState('all') // 'all' | 'published' | 'draft' | 'trash'
  
  // 搜索和排序
  const [searchQuery, setSearchQuery] = useState('')
  const [sortBy, setSortBy] = useState('recent') // 'recent' | 'oldest' | 'name' | 'status'
  const [selectedTags, setSelectedTags] = useState([]) // 标签筛选
  
  // 自动删除设置（天数）
  const [autoDeleteDays, setAutoDeleteDays] = useState(() => {
    const saved = localStorage.getItem('trashAutoDeleteDays')
    return saved ? parseInt(saved, 10) : 30 // 默认30天
  })
  
  // 数据状态
  const [createdWorlds, setCreatedWorlds] = useState([])
  const [playHistory, setPlayHistory] = useState([])
  const [myCharacters, setMyCharacters] = useState([]) // 本地存储的角色列表
  const [myStories, setMyStories] = useState([]) // 本地存储的故事列表
  const [characterStates, setCharacterStates] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  
  // 编辑主页状态
  const [isEditingProfile, setIsEditingProfile] = useState(false)
  const [editProfileData, setEditProfileData] = useState({
    displayName: '',
    bio: '',
    avatar: '',
    banner: ''
  })
  const [isSavingProfile, setIsSavingProfile] = useState(false)
  
  const userId = 'guest' // 后续可以从AuthService获取

  // 自动删除回收站中的过期项目
  useEffect(() => {
    const checkAndDeleteExpiredItems = async () => {
      if (autoDeleteDays <= 0) return // 如果设置为0或负数，不自动删除
      
      const now = Date.now()
      const expireTime = autoDeleteDays * 24 * 60 * 60 * 1000 // 转换为毫秒
      
      try {
        // 检查故事
        const stories = useStoryStore.getState().stories
        for (const story of stories) {
          if (story.status === 'trash' && story.deletedAt) {
            if (now - story.deletedAt > expireTime) {
              // 自动永久删除
              deleteStory(story.id)
              console.log('自动删除过期故事:', story.id)
            }
          }
        }
        
        // 检查角色
        const characters = await playCreateStorageService.getAllCharacterProfiles()
        for (const char of characters) {
          if (char.status === 'trash' && char.deletedAt) {
            if (now - char.deletedAt > expireTime) {
              await playCreateStorageService.deleteCharacterCompletely(char.id)
              console.log('自动删除过期角色:', char.id)
            }
          }
        }
        
        // 检查世界
        const allWorlds = await worldSimulatorStorageService.getAllWorlds()
        for (const world of allWorlds) {
          if (world.status === 'trash' && world.deletedAt) {
            if (now - world.deletedAt > expireTime) {
              await worldSimulatorStorageService.deleteWorld(world.worldId)
              console.log('自动删除过期世界:', world.worldId)
            }
          }
        }
      } catch (error) {
        console.error('自动删除检查失败:', error)
      }
    }
    
    // 每小时检查一次
    const interval = setInterval(checkAndDeleteExpiredItems, 60 * 60 * 1000)
    checkAndDeleteExpiredItems() // 立即执行一次
    
    return () => clearInterval(interval)
  }, [autoDeleteDays])

  // 简单的点赞服务（从localStorage读取）
  const getLikes = (type, id) => {
    const key = `likes_${type}_${id}`
    const data = localStorage.getItem(key)
    return data ? JSON.parse(data) : { count: 0, liked: false }
  }

  // 加载用户统计数据
  useEffect(() => {
    const loadUserStats = async () => {
      try {
        // 计算获赞数（从所有作品的总点赞数）
        let totalLikes = 0
        const allWorlds = await worldSimulatorStorageService.getAllWorlds()
        const allCharacters = await playCreateStorageService.getAllCharacterProfiles()
        const allStories = useStoryStore.getState().stories
        
        // 计算用户所有作品的点赞数
        for (const world of allWorlds) {
          if (world.creatorId === userId || world.creator?.userId === userId) {
            const likes = getLikes('world', world.worldId || world.id)
            totalLikes += likes.count || 0
          }
        }
        for (const char of allCharacters) {
          if (char.creatorId === userId || char.creator?.userId === userId) {
            const likes = getLikes('character', char.id)
            totalLikes += likes.count || 0
          }
        }
        for (const story of allStories) {
          if (story.creatorId === userId || story.creator?.userId === userId) {
            const likes = getLikes('story', story.id)
            totalLikes += likes.count || 0
          }
        }
        
        // 计算作品总数
        const worksCount = createdWorlds.length + myCharacters.length + myStories.length
        
        // TODO: 从社交服务获取粉丝和关注数
        setUserStats({
          likes: totalLikes,
          followers: 0, // 待实现
          following: 0, // 待实现
          works: worksCount
        })
      } catch (error) {
        console.error('加载用户统计数据失败:', error)
      }
    }
    
    if (!isLoading) {
      loadUserStats()
    }
  }, [isLoading, createdWorlds, myCharacters, myStories, userId])

  // 加载所有数据
  useEffect(() => {
    const loadAllData = async () => {
      try {
        setIsLoading(true)
        
        // 并行加载所有数据
        const [worldsData, sessionsData, storiesData, charactersData] = await Promise.all([
          // 加载世界列表
          (async () => {
            await loadWorlds()
            const allWorlds = await worldSimulatorStorageService.getAllWorlds()
            return allWorlds.filter(world => 
              world.creatorId === userId || 
              world.creator?.userId === userId ||
              world.creatorId === 'default-user'
            )
          })(),
          
          // 加载会话历史
          (async () => {
            const sessions = await worldSimulatorStorageService.getUserSessions(userId)
            const historyWithWorlds = await Promise.all(
              sessions.map(async (session) => {
                const world = await worldSimulatorStorageService.getWorld(session.worldId)
                return {
                  ...session,
                  world: world || { name: '未知世界', worldId: session.worldId }
                }
              })
            )
            return historyWithWorlds.sort((a, b) => b.lastActiveTime - a.lastActiveTime)
          })(),
          
          // 加载故事列表
          (async () => {
            await initializeStore()
            // 等待一下让store更新
            await new Promise(resolve => setTimeout(resolve, 200))
            // 从store获取最新的stories
            const currentStories = useStoryStore.getState().stories
            return currentStories
          })(),
          
          // 加载角色列表
          (async () => {
            const profiles = await playCreateStorageService.getAllCharacterProfiles()
            
            // 加载每个角色的状态
            const states = {}
            for (const profile of profiles) {
              try {
                const state = await playCreateStorageService.getCharacterState(profile.id)
                states[profile.id] = state
              } catch (error) {
                console.warn('加载角色状态失败:', profile.id, error)
              }
            }
            setCharacterStates(states)
            
            // 更新store
            await loadCharacters()
            
            return profiles
          })()
        ])
        
        setCreatedWorlds(worldsData)
        setPlayHistory(sessionsData)
        setMyCharacters(charactersData)
        setMyStories(storiesData)
        
      } catch (error) {
        console.error('加载数据失败:', error)
        toast.error('加载数据失败')
      } finally {
        setIsLoading(false)
      }
    }
    
    loadAllData()
  }, [loadWorlds, initializeStore, loadCharacters])

  // 初始化编辑数据
  useEffect(() => {
    if (isEditingProfile && user) {
      setEditProfileData({
        displayName: user?.profile?.displayName || user?.username || '',
        bio: user?.profile?.bio || '',
        avatar: user?.profile?.avatar || user?.avatar || '',
        banner: user?.profile?.banner || user?.banner || ''
      })
    }
  }, [isEditingProfile, user])

  // 格式化时间
  const formatTime = (timestamp) => {
    if (!timestamp) return '未知'
    const date = new Date(timestamp)
    const now = new Date()
    const diff = now - date
    
    if (diff < 60000) return '刚刚'
    if (diff < 3600000) return `${Math.floor(diff / 60000)}分钟前`
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}小时前`
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}天前`
    return date.toLocaleDateString('zh-CN')
  }

  // 格式化持续时间
  const formatDuration = (ms) => {
    if (!ms || ms <= 0) return '0分钟'
    const minutes = Math.floor(ms / 60000)
    const hours = Math.floor(minutes / 60)
    if (hours > 0) return `${hours}小时${minutes % 60}分钟`
    return `${minutes}分钟`
  }
  
  // 计算剩余天数（用于回收站自动删除倒计时）
  const getRemainingDays = (deletedAt, autoDeleteDays) => {
    if (!deletedAt || autoDeleteDays <= 0) return null
    const now = Date.now()
    const expireTime = autoDeleteDays * 24 * 60 * 60 * 1000
    const remaining = expireTime - (now - deletedAt)
    if (remaining <= 0) return 0
    return Math.ceil(remaining / (24 * 60 * 60 * 1000))
  }

  // 获取状态徽章
  const getStatusBadge = (status, type = 'story') => {
    const statusConfig = {
      story: {
        draft: { label: '草稿', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
        published: { label: '已发布', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
        reviewing: { label: '审核中', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
        trash: { label: '回收站', color: 'bg-red-500/20 text-red-400 border-red-500/30' }
      },
      world: {
        draft: { label: '草稿', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
        published: { label: '已发布', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
        trash: { label: '回收站', color: 'bg-red-500/20 text-red-400 border-red-500/30' }
      },
      character: {
        draft: { label: '草稿', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
        published: { label: '已发布', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
        trash: { label: '回收站', color: 'bg-red-500/20 text-red-400 border-red-500/30' }
      }
    }
    
    const config = statusConfig[type]?.[status] || statusConfig.story.draft
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${config.color}`}>
        {config.label}
      </span>
    )
  }

  // 筛选和排序数据
  const getFilteredAndSortedData = () => {
    let data = []
    
    switch (activeCategory) {
      case 'stories':
        data = myStories.filter(story => {
          const matchesSearch = !searchQuery || 
            story.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            story.description?.toLowerCase().includes(searchQuery.toLowerCase())
          const matchesStatus = statusFilter === 'all' || 
            (statusFilter === 'published' && story.status === 'published') ||
            (statusFilter === 'draft' && story.status === 'draft') ||
            (statusFilter === 'trash' && story.status === 'trash')
          const matchesTags = selectedTags.length === 0 || 
            selectedTags.some(tag => (story.tags || []).includes(tag))
          return matchesSearch && matchesStatus && matchesTags
        })
        break
      case 'characters':
        data = myCharacters.filter(char => {
          const matchesSearch = !searchQuery || 
            char.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            char.description?.toLowerCase().includes(searchQuery.toLowerCase())
          const matchesStatus = statusFilter === 'all' || 
            (statusFilter === 'published' && (char.isPublic || char.status === 'published')) ||
            (statusFilter === 'draft' && !char.isPublic && char.status !== 'published' && char.status !== 'trash') ||
            (statusFilter === 'trash' && char.status === 'trash')
          const matchesTags = selectedTags.length === 0 || 
            selectedTags.some(tag => (char.tags || []).includes(tag))
          return matchesSearch && matchesStatus && matchesTags
        })
        break
      case 'worlds':
        data = createdWorlds.filter(world => {
          const matchesSearch = !searchQuery || 
            world.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            world.description?.toLowerCase().includes(searchQuery.toLowerCase())
          const matchesStatus = statusFilter === 'all' || 
            (statusFilter === 'published' && world.isPublic) ||
            (statusFilter === 'draft' && !world.isPublic && world.status !== 'trash') ||
            (statusFilter === 'trash' && world.status === 'trash')
          const matchesTags = selectedTags.length === 0 || 
            selectedTags.some(tag => (world.tags || []).includes(tag))
          return matchesSearch && matchesStatus && matchesTags
        })
        break
      case 'history':
        data = playHistory.filter(item => {
          const matchesSearch = !searchQuery || 
            item.world?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.currentParagraph?.toLowerCase().includes(searchQuery.toLowerCase())
          return matchesSearch
        })
        break
    }
    
    // 排序
    data.sort((a, b) => {
      switch (sortBy) {
        case 'recent':
          const timeA = a.updatedAt || a.lastActiveTime || a.createdAt || 0
          const timeB = b.updatedAt || b.lastActiveTime || b.createdAt || 0
          return timeB - timeA
        case 'oldest':
          const timeA2 = a.createdAt || a.startTime || 0
          const timeB2 = b.createdAt || b.startTime || 0
          return timeA2 - timeB2
        case 'name':
          const nameA = a.name || a.title || a.world?.name || ''
          const nameB = b.name || b.title || b.world?.name || ''
          return nameA.localeCompare(nameB)
        case 'status':
          const statusA = a.status || (a.isPublic ? 'published' : 'draft')
          const statusB = b.status || (b.isPublic ? 'published' : 'draft')
          return statusA.localeCompare(statusB)
        default:
          return 0
      }
    })
    
    return data
  }

  // 处理操作
  const handleContinue = (session) => {
    navigate(`/world-simulator/experience/${session.worldId}`, {
      state: { sessionId: session.sessionId }
    })
  }

  const handleDeleteSession = async (sessionId) => {
    if (!confirm('确定要删除这个游戏记录吗？')) return
    
    try {
      await worldSimulatorStorageService.deleteSession(sessionId)
      toast.success('删除成功')
      const sessions = await worldSimulatorStorageService.getUserSessions(userId)
      const historyWithWorlds = await Promise.all(
        sessions.map(async (session) => {
          const world = await worldSimulatorStorageService.getWorld(session.worldId)
          return {
            ...session,
            world: world || { name: '未知世界', worldId: session.worldId }
          }
        })
      )
      historyWithWorlds.sort((a, b) => b.lastActiveTime - a.lastActiveTime)
      setPlayHistory(historyWithWorlds)
    } catch (error) {
      console.error('删除会话失败:', error)
      toast.error('删除失败')
    }
  }

  // 删除故事（移到回收站）
  const handleDeleteStory = async (storyId) => {
    try {
      const story = myStories.find(s => s.id === storyId)
      if (!story) return
      
      // 移到回收站，添加deletedAt时间戳
      await updateStory(storyId, { 
        status: 'trash',
        deletedAt: Date.now()
      })
      toast.success('已移至回收站')
      // 等待store更新后刷新本地状态
      setTimeout(() => {
        const currentStories = useStoryStore.getState().stories
        setMyStories(currentStories)
      }, 200)
    } catch (error) {
      console.error('移至回收站失败:', error)
      toast.error('操作失败')
    }
  }

  const handlePublishStory = async (storyId) => {
    try {
      await updateStory(storyId, { status: 'published', publishedAt: Date.now() })
      toast.success('已发布')
      setTimeout(() => {
        const currentStories = useStoryStore.getState().stories
        setMyStories(currentStories)
      }, 200)
    } catch (error) {
      console.error('发布故事失败:', error)
      toast.error('发布失败')
    }
  }
  
  // 永久删除故事
  const handlePermanentDeleteStory = async (storyId) => {
    if (!confirm('确定要永久删除这个故事吗？此操作不可恢复！')) return
    
    try {
      deleteStory(storyId)
      toast.success('已永久删除')
      await initializeStore()
      setTimeout(() => {
        const currentStories = useStoryStore.getState().stories
        setMyStories(currentStories)
      }, 200)
    } catch (error) {
      console.error('永久删除失败:', error)
      toast.error('删除失败')
    }
  }

  // 删除角色（移到回收站）
  const handleDeleteCharacter = async (characterId) => {
    try {
      const character = myCharacters.find(c => c.id === characterId)
      if (!character) return
      
      // 移到回收站，添加deletedAt时间戳
      const updatedCharacter = { 
        ...character, 
        status: 'trash',
        deletedAt: Date.now()
      }
      await playCreateStorageService.saveCharacterProfile(updatedCharacter)
      toast.success('已移至回收站')
      // 重新加载角色列表
      const profiles = await playCreateStorageService.getAllCharacterProfiles()
      setMyCharacters(profiles)
      await loadCharacters()
    } catch (error) {
      console.error('移至回收站失败:', error)
      toast.error('操作失败')
    }
  }

  const handlePublishCharacter = async (characterId) => {
    try {
      const character = myCharacters.find(c => c.id === characterId)
      if (!character) return
      const updatedCharacter = {
        ...character,
        status: 'published',
        isPublic: true,
        publishedAt: Date.now()
      }
      await playCreateStorageService.saveCharacterProfile(updatedCharacter)
      toast.success('角色已发布')
      const profiles = await playCreateStorageService.getAllCharacterProfiles()
      setMyCharacters(profiles)
      await loadCharacters()
    } catch (error) {
      console.error('发布角色失败:', error)
      toast.error('发布失败')
    }
  }
  
  // 永久删除角色
  const handlePermanentDeleteCharacter = async (characterId) => {
    if (!confirm('确定要永久删除这个角色吗？此操作不可恢复！')) return
    
    try {
      await playCreateStorageService.deleteCharacterCompletely(characterId)
      toast.success('已永久删除')
      const profiles = await playCreateStorageService.getAllCharacterProfiles()
      setMyCharacters(profiles)
      await loadCharacters()
    } catch (error) {
      console.error('永久删除失败:', error)
      toast.error('删除失败')
    }
  }

  const handleMoveCharacterToTrash = async (character) => {
    try {
      // 更新角色状态为trash
      const updatedCharacter = { ...character, status: 'trash' }
      await playCreateStorageService.saveCharacterProfile(updatedCharacter)
      toast.success('已移至回收站')
      // 重新加载角色列表
      const profiles = await playCreateStorageService.getAllCharacterProfiles()
      setMyCharacters(profiles)
      await loadCharacters()
    } catch (error) {
      console.error('移至回收站失败:', error)
      toast.error('操作失败')
    }
  }

  const handleRestoreCharacter = async (character) => {
    try {
      // 恢复角色（移除trash状态）
      const updatedCharacter = { ...character, status: 'draft' }
      await playCreateStorageService.saveCharacterProfile(updatedCharacter)
      toast.success('已恢复')
      // 重新加载角色列表
      const profiles = await playCreateStorageService.getAllCharacterProfiles()
      setMyCharacters(profiles)
      await loadCharacters()
    } catch (error) {
      console.error('恢复失败:', error)
      toast.error('操作失败')
    }
  }

  // 删除世界（移到回收站）
  const handleDeleteWorld = async (worldId) => {
    try {
      // 移到回收站，添加deletedAt时间戳
      await worldSimulatorStorageService.updateWorld(worldId, { 
        status: 'trash',
        deletedAt: Date.now()
      })
      toast.success('已移至回收站')
      const allWorlds = await worldSimulatorStorageService.getAllWorlds()
      const myWorlds = allWorlds.filter(world => 
        world.creatorId === userId || 
        world.creator?.userId === userId ||
        world.creatorId === 'default-user'
      )
      setCreatedWorlds(myWorlds)
    } catch (error) {
      console.error('移至回收站失败:', error)
      toast.error('操作失败')
    }
  }

  const handlePublishWorld = async (worldId) => {
    try {
      await worldSimulatorStorageService.updateWorld(worldId, {
        status: 'published',
        isPublic: true,
        publishedAt: Date.now()
      })
      toast.success('世界已发布')
      const allWorlds = await worldSimulatorStorageService.getAllWorlds()
      const myWorlds = allWorlds.filter(world => 
        world.creatorId === userId || 
        world.creator?.userId === userId ||
        world.creatorId === 'default-user'
      )
      setCreatedWorlds(myWorlds)
    } catch (error) {
      console.error('发布世界失败:', error)
      toast.error('发布失败')
    }
  }
  
  // 永久删除世界
  const handlePermanentDeleteWorld = async (worldId) => {
    if (!confirm('确定要永久删除这个世界吗？此操作不可恢复！')) return
    
    try {
      await worldSimulatorStorageService.deleteWorld(worldId)
      toast.success('已永久删除')
      const allWorlds = await worldSimulatorStorageService.getAllWorlds()
      const myWorlds = allWorlds.filter(world => 
        world.creatorId === userId || 
        world.creator?.userId === userId ||
        world.creatorId === 'default-user'
      )
      setCreatedWorlds(myWorlds)
    } catch (error) {
      console.error('永久删除失败:', error)
      toast.error('删除失败')
    }
  }

  const handleMoveToTrash = async (item, type) => {
    try {
      if (type === 'story') {
        // 更新故事状态为trash
        await updateStory(item.id, { status: 'trash' })
        toast.success('已移至回收站')
        // 等待store更新后刷新本地状态
        setTimeout(() => {
          const currentStories = useStoryStore.getState().stories
          setMyStories(currentStories)
        }, 200)
      } else if (type === 'world') {
        await worldSimulatorStorageService.updateWorld(item.worldId, { status: 'trash' })
        toast.success('已移至回收站')
        const allWorlds = await worldSimulatorStorageService.getAllWorlds()
        const myWorlds = allWorlds.filter(world => 
          world.creatorId === userId || 
          world.creator?.userId === userId ||
          world.creatorId === 'default-user'
        )
        setCreatedWorlds(myWorlds)
      }
    } catch (error) {
      console.error('移至回收站失败:', error)
      toast.error('操作失败')
    }
  }

  const handleRestore = async (item, type) => {
    try {
      if (type === 'story') {
        // 恢复故事
        await updateStory(item.id, { status: 'draft' })
        toast.success('已恢复')
        // 等待store更新后刷新本地状态
        setTimeout(() => {
          const currentStories = useStoryStore.getState().stories
          setMyStories(currentStories)
        }, 200)
      } else if (type === 'world') {
        await worldSimulatorStorageService.updateWorld(item.worldId, { status: 'draft' })
        toast.success('已恢复')
        const allWorlds = await worldSimulatorStorageService.getAllWorlds()
        const myWorlds = allWorlds.filter(world => 
          world.creatorId === userId || 
          world.creator?.userId === userId ||
          world.creatorId === 'default-user'
        )
        setCreatedWorlds(myWorlds)
      }
    } catch (error) {
      console.error('恢复失败:', error)
      toast.error('操作失败')
    }
  }

  // 获取统计数据
  const getStats = () => {
    const stats = {
      stories: {
        total: myStories.length,
        published: myStories.filter(s => s.status === 'published').length,
        draft: myStories.filter(s => s.status === 'draft').length,
        trash: myStories.filter(s => s.status === 'trash').length
      },
      characters: {
        total: myCharacters.length,
        published: myCharacters.filter(c => c.isPublic || c.status === 'published').length,
        draft: myCharacters.filter(c => !c.isPublic && c.status !== 'published' && c.status !== 'trash').length,
        trash: myCharacters.filter(c => c.status === 'trash').length
      },
      worlds: {
        total: createdWorlds.length,
        published: createdWorlds.filter(w => w.isPublic).length,
        draft: createdWorlds.filter(w => !w.isPublic && w.status !== 'trash').length,
        trash: createdWorlds.filter(w => w.status === 'trash').length
      },
      history: {
        total: playHistory.length,
        active: playHistory.filter(h => h.status === 'active').length
      }
    }
    return stats
  }

  const stats = getStats()
  const filteredData = getFilteredAndSortedData()

  if (isLoading) {
    return (
      <div className="min-h-screen bg-dark-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-dark-300">加载中...</p>
        </div>
      </div>
    )
  }

  // 获取用户显示信息
  const displayName = user?.profile?.displayName || user?.username || '用户'
  const userBio = user?.profile?.bio || '这个人很懒，什么都没有留下...'
  const userAvatar = user?.profile?.avatar || user?.avatar
  const userBanner = user?.profile?.banner || user?.banner // 背景横幅图

  // 处理头像上传
  const handleAvatarUpload = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    try {
      // 检查文件大小（限制为5MB）
      if (file.size > 5 * 1024 * 1024) {
        toast.error('图片文件过大，请选择小于5MB的图片')
        return
      }

      // 检查文件类型
      if (!file.type.startsWith('image/')) {
        toast.error('请选择图片文件')
        return
      }

      toast.loading('正在上传头像...')

      // 生成唯一的文件名
      const fileName = `avatar-${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${file.name.split('.').pop()}`
      
      // 保存图片到本地云存储
      const imageResult = await localCloudService.saveImageFile(fileName, file)
      
      if (imageResult && imageResult.url) {
        setEditProfileData(prev => ({
          ...prev,
          avatar: imageResult.url
        }))
        toast.dismiss()
        toast.success('头像上传成功！')
      } else {
        throw new Error('图片保存失败或未返回URL')
      }
    } catch (error) {
      console.error('头像上传失败:', error)
      toast.dismiss()
      toast.error('头像上传失败')
    }
  }

  // 处理背景图上传
  const handleBannerUpload = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    try {
      // 检查文件大小（限制为5MB）
      if (file.size > 5 * 1024 * 1024) {
        toast.error('图片文件过大，请选择小于5MB的图片')
        return
      }

      // 检查文件类型
      if (!file.type.startsWith('image/')) {
        toast.error('请选择图片文件')
        return
      }

      toast.loading('正在上传背景图...')

      // 生成唯一的文件名
      const fileName = `banner-${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${file.name.split('.').pop()}`
      
      // 保存图片到本地云存储
      const imageResult = await localCloudService.saveImageFile(fileName, file)
      
      if (imageResult && imageResult.url) {
        setEditProfileData(prev => ({
          ...prev,
          banner: imageResult.url
        }))
        toast.dismiss()
        toast.success('背景图上传成功！')
      } else {
        throw new Error('图片保存失败或未返回URL')
      }
    } catch (error) {
      console.error('背景图上传失败:', error)
      toast.dismiss()
      toast.error('背景图上传失败')
    }
  }

  // 保存个人资料
  const handleSaveProfile = async () => {
    try {
      setIsSavingProfile(true)
      
      await updateProfile({
        profile: {
          displayName: editProfileData.displayName,
          bio: editProfileData.bio,
          avatar: editProfileData.avatar,
          banner: editProfileData.banner
        }
      })

      // 刷新用户数据
      await refreshUser()
      
      toast.success('个人资料更新成功！')
      setIsEditingProfile(false)
    } catch (error) {
      console.error('保存个人资料失败:', error)
      toast.error('保存失败，请重试')
    } finally {
      setIsSavingProfile(false)
    }
  }

  return (
    <div className="min-h-screen bg-dark-900">
      {/* 顶部栏 */}
      <div className="sticky top-16 z-40 bg-dark-900/95 backdrop-blur-sm border-b border-dark-700">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <User className="w-8 h-8 text-primary" />
            <h1 className="text-2xl font-bold text-white font-display">个人主页</h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto">
        {/* 顶部背景横幅 */}
        <div className="relative w-full h-64 overflow-hidden">
          {userBanner ? (
            <img 
              src={userBanner} 
              alt="背景横幅"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-primary/20 via-secondary/20 to-purple-500/20">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/30 via-transparent to-secondary/30"></div>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-dark-900 via-dark-900/50 to-transparent"></div>
        </div>

        {/* 用户信息区域 - 头像部分重叠背景 */}
        <div className="relative px-4 -mt-20 mb-6">
          <div className="flex flex-col md:flex-row gap-6">
            {/* 头像 - 部分重叠背景横幅 */}
            <div className="flex-shrink-0 relative z-10">
              {userAvatar ? (
                <img 
                  src={userAvatar} 
                  alt={displayName}
                  className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover border-4 border-dark-900 shadow-xl"
                />
              ) : (
                <div className="w-32 h-32 md:w-40 md:h-40 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white text-4xl md:text-5xl font-bold border-4 border-dark-900 shadow-xl">
                  {displayName.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            
            {/* 用户基本信息 */}
            <div className="flex-1 pt-20 md:pt-24">
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-3xl font-bold text-white">{displayName}</h2>
                {isAuthenticated && (
                  <button
                    onClick={() => setIsEditingProfile(true)}
                    className="p-2 bg-dark-800 hover:bg-dark-700 text-white rounded-lg transition-colors border border-dark-600 hover:border-primary/50"
                    title="编辑主页"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                )}
              </div>
              <p className="text-dark-300 mb-6">{userBio}</p>
              
              {/* 统计数据 */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-dark-800/80 backdrop-blur-sm rounded-lg p-4 border border-dark-700 hover:border-primary/50 transition-colors">
                  <div className="flex items-center gap-2 mb-2">
                    <Heart className="w-5 h-5 text-red-400" />
                    <span className="text-sm text-dark-400">获赞</span>
                  </div>
                  <div className="text-2xl font-bold text-white">{userStats.likes}</div>
                </div>
                <div className="bg-dark-800/80 backdrop-blur-sm rounded-lg p-4 border border-dark-700 hover:border-primary/50 transition-colors">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-5 h-5 text-blue-400" />
                    <span className="text-sm text-dark-400">粉丝</span>
                  </div>
                  <div className="text-2xl font-bold text-white">{userStats.followers}</div>
                </div>
                <div className="bg-dark-800/80 backdrop-blur-sm rounded-lg p-4 border border-dark-700 hover:border-primary/50 transition-colors">
                  <div className="flex items-center gap-2 mb-2">
                    <UserPlus className="w-5 h-5 text-green-400" />
                    <span className="text-sm text-dark-400">关注</span>
                  </div>
                  <div className="text-2xl font-bold text-white">{userStats.following}</div>
                </div>
                <div className="bg-dark-800/80 backdrop-blur-sm rounded-lg p-4 border border-dark-700 hover:border-primary/50 transition-colors">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-5 h-5 text-yellow-400" />
                    <span className="text-sm text-dark-400">作品</span>
                  </div>
                  <div className="text-2xl font-bold text-white">{userStats.works}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="px-4">

        {/* 创作入口 - 放在开始创作按钮下面 */}
        <div className="mb-6">
          <CreationEntry />
        </div>

        {/* 分类标签和创建按钮 - 合并到同一行 */}
        <div className="flex items-center justify-between gap-4 mb-6 border-b border-dark-700 pb-3">
          <div className="flex gap-4 overflow-x-auto flex-1">
            <button
              onClick={() => {
                setActiveCategory('stories')
                setStatusFilter('all')
                setSearchParams({ tab: 'stories' }, { replace: true })
              }}
              className={`px-6 py-3 font-medium transition-colors border-b-2 whitespace-nowrap ${
                activeCategory === 'stories'
                  ? 'text-primary border-primary'
                  : 'text-dark-300 border-transparent hover:text-white'
              }`}
            >
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                我的故事 ({stats.stories.total})
              </div>
            </button>
            <button
              onClick={() => {
                setActiveCategory('characters')
                setStatusFilter('all')
                setSearchParams({ tab: 'characters' }, { replace: true })
              }}
              className={`px-6 py-3 font-medium transition-colors border-b-2 whitespace-nowrap ${
                activeCategory === 'characters'
                  ? 'text-primary border-primary'
                  : 'text-dark-300 border-transparent hover:text-white'
              }`}
            >
              <div className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                我的角色 ({stats.characters.total})
              </div>
            </button>
            <button
              onClick={() => {
                setActiveCategory('worlds')
                setStatusFilter('all')
                setSearchParams({ tab: 'worlds' }, { replace: true })
              }}
              className={`px-6 py-3 font-medium transition-colors border-b-2 whitespace-nowrap ${
                activeCategory === 'worlds'
                  ? 'text-primary border-primary'
                  : 'text-dark-300 border-transparent hover:text-white'
              }`}
            >
              <div className="flex items-center gap-2">
                <Globe className="w-5 h-5" />
                我创建的世界 ({stats.worlds.total})
              </div>
            </button>
            <button
              onClick={() => {
                setActiveCategory('history')
                setStatusFilter('all')
                setSearchParams({ tab: 'history' }, { replace: true })
              }}
              className={`px-6 py-3 font-medium transition-colors border-b-2 whitespace-nowrap ${
                activeCategory === 'history'
                  ? 'text-primary border-primary'
                  : 'text-dark-300 border-transparent hover:text-white'
              }`}
            >
              <div className="flex items-center gap-2">
                <History className="w-5 h-5" />
                游戏历史 ({stats.history.total})
              </div>
            </button>
          </div>

          {/* 创建按钮 - 根据当前标签页显示，放在标签右侧 */}
          {activeCategory === 'stories' && (
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors text-sm font-medium flex items-center gap-2 whitespace-nowrap flex-shrink-0"
            >
              <Plus className="w-4 h-4" />
              创建故事
            </button>
          )}
          {activeCategory === 'characters' && (
            <button
              onClick={() => navigate('/play-create')}
              className="px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors text-sm font-medium flex items-center gap-2 whitespace-nowrap flex-shrink-0"
            >
              <Plus className="w-4 h-4" />
              创建角色
            </button>
          )}
          {activeCategory === 'worlds' && (
            <button
              onClick={() => navigate('/world-simulator/create')}
              className="px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors text-sm font-medium flex items-center gap-2 whitespace-nowrap flex-shrink-0"
            >
              <Plus className="w-4 h-4" />
              创建世界
            </button>
          )}
        </div>

        {/* 搜索和筛选工具栏 */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-dark-400" />
            <input
              type="text"
              placeholder="搜索..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:border-primary"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-dark-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          
          {/* 状态筛选（故事、角色和世界支持） */}
          {(activeCategory === 'stories' || activeCategory === 'characters' || activeCategory === 'worlds') && (
            <div className="flex gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary"
              >
                <option value="all">全部状态</option>
                <option value="published">已发布</option>
                <option value="draft">草稿</option>
                <option value="trash">回收站</option>
              </select>
              
              {/* 回收站自动删除设置 */}
              {statusFilter === 'trash' && (
                <div className="flex items-center gap-2 px-4 py-2 bg-dark-800 border border-dark-600 rounded-lg">
                  <span className="text-sm text-dark-300 whitespace-nowrap">自动删除：</span>
                  <input
                    type="number"
                    min="0"
                    max="365"
                    value={autoDeleteDays}
                    onChange={(e) => {
                      const days = parseInt(e.target.value, 10) || 0
                      setAutoDeleteDays(days)
                      localStorage.setItem('trashAutoDeleteDays', days.toString())
                    }}
                    className="w-16 px-2 py-1 bg-dark-700 border border-dark-600 rounded text-white text-sm focus:outline-none focus:border-primary"
                    placeholder="天数"
                  />
                  <span className="text-sm text-dark-400 whitespace-nowrap">天后</span>
                  {autoDeleteDays === 0 && (
                    <span className="text-xs text-yellow-400 whitespace-nowrap">（不自动删除）</span>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* 排序 */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 bg-dark-800 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary"
          >
            <option value="recent">最近更新</option>
            <option value="oldest">最早创建</option>
            <option value="name">按名称</option>
            {activeCategory !== 'history' && <option value="status">按状态</option>}
          </select>
        </div>

        {/* 统计卡片 */}
        {activeCategory === 'stories' && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <button
              onClick={() => setStatusFilter('all')}
              className={`bg-dark-800 rounded-lg p-4 border transition-all cursor-pointer hover:border-primary/50 ${
                statusFilter === 'all' ? 'border-primary' : 'border-dark-700'
              }`}
            >
              <div className="text-sm text-dark-300 mb-1">总故事数</div>
              <div className="text-2xl font-bold text-white">{stats.stories.total}</div>
            </button>
            <button
              onClick={() => setStatusFilter('published')}
              className={`bg-dark-800 rounded-lg p-4 border transition-all cursor-pointer hover:border-primary/50 ${
                statusFilter === 'published' ? 'border-primary' : 'border-dark-700'
              }`}
            >
              <div className="text-sm text-dark-300 mb-1">已发布</div>
              <div className="text-2xl font-bold text-green-400">{stats.stories.published}</div>
            </button>
            <button
              onClick={() => setStatusFilter('draft')}
              className={`bg-dark-800 rounded-lg p-4 border transition-all cursor-pointer hover:border-primary/50 ${
                statusFilter === 'draft' ? 'border-primary' : 'border-dark-700'
              }`}
            >
              <div className="text-sm text-dark-300 mb-1">草稿</div>
              <div className="text-2xl font-bold text-yellow-400">{stats.stories.draft}</div>
            </button>
            <button
              onClick={() => setStatusFilter('trash')}
              className={`bg-dark-800 rounded-lg p-4 border transition-all cursor-pointer hover:border-primary/50 ${
                statusFilter === 'trash' ? 'border-primary' : 'border-dark-700'
              }`}
            >
              <div className="text-sm text-dark-300 mb-1">回收站</div>
              <div className="text-2xl font-bold text-red-400">{stats.stories.trash}</div>
            </button>
          </div>
        )}

        {activeCategory === 'characters' && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <button
              onClick={() => setStatusFilter('all')}
              className={`bg-dark-800 rounded-lg p-4 border transition-all cursor-pointer hover:border-primary/50 ${
                statusFilter === 'all' ? 'border-primary' : 'border-dark-700'
              }`}
            >
              <div className="text-sm text-dark-300 mb-1">总角色数</div>
              <div className="text-2xl font-bold text-white">{stats.characters.total}</div>
            </button>
            <button
              onClick={() => setStatusFilter('published')}
              className={`bg-dark-800 rounded-lg p-4 border transition-all cursor-pointer hover:border-primary/50 ${
                statusFilter === 'published' ? 'border-primary' : 'border-dark-700'
              }`}
            >
              <div className="text-sm text-dark-300 mb-1">已发布</div>
              <div className="text-2xl font-bold text-green-400">{stats.characters.published}</div>
            </button>
            <button
              onClick={() => setStatusFilter('draft')}
              className={`bg-dark-800 rounded-lg p-4 border transition-all cursor-pointer hover:border-primary/50 ${
                statusFilter === 'draft' ? 'border-primary' : 'border-dark-700'
              }`}
            >
              <div className="text-sm text-dark-300 mb-1">草稿</div>
              <div className="text-2xl font-bold text-yellow-400">{stats.characters.draft}</div>
            </button>
            <button
              onClick={() => setStatusFilter('trash')}
              className={`bg-dark-800 rounded-lg p-4 border transition-all cursor-pointer hover:border-primary/50 ${
                statusFilter === 'trash' ? 'border-primary' : 'border-dark-700'
              }`}
            >
              <div className="text-sm text-dark-300 mb-1">回收站</div>
              <div className="text-2xl font-bold text-red-400">{stats.characters.trash}</div>
            </button>
          </div>
        )}

        {activeCategory === 'worlds' && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <button
              onClick={() => setStatusFilter('all')}
              className={`bg-dark-800 rounded-lg p-4 border transition-all cursor-pointer hover:border-primary/50 ${
                statusFilter === 'all' ? 'border-primary' : 'border-dark-700'
              }`}
            >
              <div className="text-sm text-dark-300 mb-1">总世界数</div>
              <div className="text-2xl font-bold text-white">{stats.worlds.total}</div>
            </button>
            <button
              onClick={() => setStatusFilter('published')}
              className={`bg-dark-800 rounded-lg p-4 border transition-all cursor-pointer hover:border-primary/50 ${
                statusFilter === 'published' ? 'border-primary' : 'border-dark-700'
              }`}
            >
              <div className="text-sm text-dark-300 mb-1">已发布</div>
              <div className="text-2xl font-bold text-green-400">{stats.worlds.published}</div>
            </button>
            <button
              onClick={() => setStatusFilter('draft')}
              className={`bg-dark-800 rounded-lg p-4 border transition-all cursor-pointer hover:border-primary/50 ${
                statusFilter === 'draft' ? 'border-primary' : 'border-dark-700'
              }`}
            >
              <div className="text-sm text-dark-300 mb-1">草稿</div>
              <div className="text-2xl font-bold text-yellow-400">{stats.worlds.draft}</div>
            </button>
            <button
              onClick={() => setStatusFilter('trash')}
              className={`bg-dark-800 rounded-lg p-4 border transition-all cursor-pointer hover:border-primary/50 ${
                statusFilter === 'trash' ? 'border-primary' : 'border-dark-700'
              }`}
            >
              <div className="text-sm text-dark-300 mb-1">回收站</div>
              <div className="text-2xl font-bold text-red-400">{stats.worlds.trash}</div>
            </button>
          </div>
        )}

        {/* 内容列表 */}
        {filteredData.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
            {activeCategory === 'stories' && (
              <>
                <FileText className="w-16 h-16 text-dark-600 mb-4" />
                <h2 className="text-xl font-semibold text-dark-300 mb-2">
                  {searchQuery || statusFilter !== 'all' ? '没有找到匹配的故事' : '还没有创建故事'}
                </h2>
                <p className="text-dark-400 mb-6">
                  {searchQuery || statusFilter !== 'all' ? '尝试调整搜索条件或筛选器' : '开始创建你的第一个互动故事吧！'}
                </p>
                {!searchQuery && statusFilter === 'all' && (
                  <button
                    onClick={() => navigate('/')}
                    className="px-6 py-3 bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors flex items-center gap-2"
                  >
                    <Plus className="w-5 h-5" />
                    创建故事
                  </button>
                )}
              </>
            )}
            {activeCategory === 'characters' && (
              <>
                <MessageCircle className="w-16 h-16 text-dark-600 mb-4" />
                <h2 className="text-xl font-semibold text-dark-300 mb-2">
                  {searchQuery || statusFilter !== 'all' ? '没有找到匹配的角色' : '还没有创建角色'}
                </h2>
                <p className="text-dark-400 mb-6">
                  {searchQuery || statusFilter !== 'all' ? '尝试调整搜索条件或筛选器' : '开始创建你的第一个AI角色吧！'}
                </p>
                {!searchQuery && statusFilter === 'all' && (
                  <button
                    onClick={() => navigate('/play-create')}
                    className="px-6 py-3 bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors flex items-center gap-2"
                  >
                    <Plus className="w-5 h-5" />
                    创建角色
                  </button>
                )}
              </>
            )}
            {activeCategory === 'worlds' && (
              <>
                <Globe className="w-16 h-16 text-dark-600 mb-4" />
                <h2 className="text-xl font-semibold text-dark-300 mb-2">
                  {searchQuery || statusFilter !== 'all' ? '没有找到匹配的世界' : '还没有创建世界'}
                </h2>
                <p className="text-dark-400 mb-6">
                  {searchQuery || statusFilter !== 'all' ? '尝试调整搜索条件或筛选器' : '开始创建你的第一个世界吧！'}
                </p>
                {!searchQuery && statusFilter === 'all' && (
                  <button
                    onClick={() => navigate('/world-simulator/create')}
                    className="px-6 py-3 bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors flex items-center gap-2"
                  >
                    <Plus className="w-5 h-5" />
                    创建世界
                  </button>
                )}
              </>
            )}
            {activeCategory === 'history' && (
              <>
                <History className="w-16 h-16 text-dark-600 mb-4" />
                <h2 className="text-xl font-semibold text-dark-300 mb-2">
                  {searchQuery ? '没有找到匹配的记录' : '还没有游戏记录'}
                </h2>
                <p className="text-dark-400 mb-6">
                  {searchQuery ? '尝试调整搜索条件' : '开始体验世界，你的游戏记录会显示在这里'}
                </p>
                {!searchQuery && (
                  <button
                    onClick={() => navigate('/world-simulator')}
                    className="px-6 py-3 bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors flex items-center gap-2"
                  >
                    <Globe className="w-5 h-5" />
                    浏览世界
                  </button>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* 故事列表 */}
            {activeCategory === 'stories' && filteredData.map((story) => (
              <UnifiedCard
                key={story.id}
                item={story}
                type="story"
                onClick={(item, type) => {
                  // 已发布的内容跳转到详情页，其他跳转到编辑/预览
                  if (item.status === 'published') {
                    navigate(`/content/story/${item.id}`)
                  } else {
                    navigate(`/player/${item.id}`)
                  }
                }}
                onPlay={(item) => navigate(`/player/${item.id}`)}
                onDelete={(item, type, isTrash) => {
                  if (isTrash) {
                    handlePermanentDeleteStory(item.id)
                  } else {
                    handleDeleteStory(item.id)
                  }
                }}
                onPublish={(item) => handlePublishStory(item.id)}
                getStatusBadge={getStatusBadge}
                formatTime={formatTime}
                autoDeleteDays={statusFilter === 'trash' ? autoDeleteDays : 0}
                getRemainingDays={getRemainingDays}
              />
            ))}

            {/* 角色列表 */}
            {activeCategory === 'characters' && filteredData.map((character) => (
              <UnifiedCard
                key={character.id}
                item={character}
                type="character"
                onClick={(item, type) => {
                  // 已发布的内容跳转到详情页，其他跳转到对话
                  if (item.isPublic || item.status === 'published') {
                    navigate(`/content/character/${item.id}`)
                  } else {
                    navigate(`/play-create/chat/${item.id}`)
                  }
                }}
                onPlay={(item) => navigate(`/play-create/chat/${item.id}`)}
                onDelete={(item, type, isTrash) => {
                  if (isTrash) {
                    handlePermanentDeleteCharacter(item.id)
                  } else {
                    handleDeleteCharacter(item.id)
                  }
                }}
                onPublish={(item) => handlePublishCharacter(item.id)}
                getStatusBadge={getStatusBadge}
                formatTime={formatTime}
                characterState={characterStates[character.id]}
                autoDeleteDays={statusFilter === 'trash' ? autoDeleteDays : 0}
                getRemainingDays={getRemainingDays}
              />
            ))}

            {/* 世界列表 */}
            {activeCategory === 'worlds' && filteredData.map((world) => (
              <UnifiedCard
                key={world.worldId}
                item={world}
                type="world"
                onClick={(item, type) => {
                  // 统一跳转到新的详情页（带评论、点赞等社交功能）
                  navigate(`/content/world/${item.worldId || item.id}`)
                }}
                onPlay={(item) => navigate(`/content/world/${item.worldId || item.id}`)}
                onDelete={(item, type, isTrash) => {
                  if (isTrash) {
                    handlePermanentDeleteWorld(item.worldId || item.id)
                  } else {
                    handleDeleteWorld(item.worldId || item.id)
                  }
                }}
                onPublish={(item) => handlePublishWorld(item.worldId || item.id)}
                getStatusBadge={getStatusBadge}
                formatTime={formatTime}
                autoDeleteDays={statusFilter === 'trash' ? autoDeleteDays : 0}
                getRemainingDays={getRemainingDays}
              />
            ))}

            {/* 历史记录列表 */}
            {activeCategory === 'history' && filteredData.map((item) => (
              <div
                key={item.sessionId}
                className="bg-dark-800 rounded-xl p-6 border border-dark-700 hover:border-primary/50 transition-all"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-primary" />
                    <h3 className="text-lg font-bold text-white">
                      {item.world?.name || '未知世界'}
                    </h3>
                    {item.status === 'active' && (
                      <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded text-xs border border-green-500/30">
                        进行中
                      </span>
                    )}
                  </div>
                </div>
                
                {item.currentParagraph && (
                  <p className="text-sm text-dark-300 mb-4 line-clamp-2">
                    {item.currentParagraph}
                  </p>
                )}

                <div className="flex items-center gap-6 text-xs text-dark-400 mb-4">
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span>最后活跃：{formatTime(item.lastActiveTime)}</span>
                  </div>
                  {item.totalDuration > 0 && (
                    <div className="flex items-center gap-1">
                      <BookOpen className="w-4 h-4" />
                      <span>游戏时长：{formatDuration(item.totalDuration)}</span>
                    </div>
                  )}
                  {item.conversationHistory && (
                    <span>对话：{item.conversationHistory.length} 条</span>
                  )}
                </div>

                <div className="flex gap-2">
                  {item.status === 'active' && (
                    <button
                      onClick={() => handleContinue(item)}
                      className="flex-1 px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors text-sm font-medium flex items-center justify-center gap-2"
                    >
                      <Play className="w-4 h-4" />
                      继续
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteSession(item.sessionId)}
                    className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
                    title="删除记录"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        </div>
      </div>

      {/* 编辑主页模态框 */}
      {isEditingProfile && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-dark-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-dark-600">
            {/* 模态框头部 */}
            <div className="sticky top-0 bg-dark-800 border-b border-dark-700 p-6 flex items-center justify-between z-10">
              <h2 className="text-2xl font-bold text-white">编辑主页</h2>
              <button
                onClick={() => setIsEditingProfile(false)}
                className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* 模态框内容 */}
            <div className="p-6 space-y-6">
              {/* 背景图编辑 */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">背景横幅</label>
                <div className="relative w-full h-48 rounded-lg overflow-hidden border border-dark-600">
                  {editProfileData.banner ? (
                    <img 
                      src={editProfileData.banner} 
                      alt="背景横幅"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-primary/20 via-secondary/20 to-purple-500/20 flex items-center justify-center">
                      <span className="text-dark-400">暂无背景图</span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                    <label className="px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg cursor-pointer transition-colors flex items-center gap-2">
                      <Camera className="w-4 h-4" />
                      上传背景图
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleBannerUpload}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>
              </div>

              {/* 头像编辑 */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">头像</label>
                <div className="flex items-center gap-4">
                  <div className="relative w-24 h-24 rounded-full overflow-hidden border-4 border-dark-600">
                    {editProfileData.avatar ? (
                      <img 
                        src={editProfileData.avatar} 
                        alt="头像"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white text-2xl font-bold">
                        {editProfileData.displayName?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                    )}
                  </div>
                  <label className="px-4 py-2 bg-dark-700 hover:bg-dark-600 text-white rounded-lg cursor-pointer transition-colors flex items-center gap-2">
                    <Camera className="w-4 h-4" />
                    上传头像
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarUpload}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              {/* 显示名称 */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">显示名称</label>
                <input
                  type="text"
                  value={editProfileData.displayName}
                  onChange={(e) => setEditProfileData(prev => ({ ...prev, displayName: e.target.value }))}
                  className="w-full px-4 py-2 bg-dark-900 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary"
                  placeholder="请输入显示名称"
                />
              </div>

              {/* 个人简介 */}
              <div>
                <label className="block text-sm font-medium text-white mb-2">个人简介</label>
                <textarea
                  value={editProfileData.bio}
                  onChange={(e) => setEditProfileData(prev => ({ ...prev, bio: e.target.value }))}
                  rows={4}
                  className="w-full px-4 py-2 bg-dark-900 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary resize-none"
                  placeholder="介绍一下自己..."
                />
              </div>

              {/* 操作按钮 */}
              <div className="flex gap-3 pt-4 border-t border-dark-700">
                <button
                  onClick={handleSaveProfile}
                  disabled={isSavingProfile}
                  className="flex-1 px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSavingProfile ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      保存中...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      保存
                    </>
                  )}
                </button>
                <button
                  onClick={() => setIsEditingProfile(false)}
                  className="px-4 py-2 bg-dark-700 hover:bg-dark-600 text-white rounded-lg transition-colors"
                >
                  取消
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

